import { useState } from 'react';
import { Link } from 'react-router';
import { Archive, FolderPlus } from 'lucide-react';
import { PERMISSIONS } from '@virtuetasks/types';
import { PageHeader } from '@/app/layout/AppShell';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/shared/EmptyState';
import { useCan } from '@/features/auth';
import { useUiStore } from '@/stores/ui';
import { useProjects, type ProjectView } from './api';

function ProjectCard({ project }: { project: ProjectView }) {
  const { stats } = project;
  return (
    <Link
      // V4 F-1: from the grid you are asking "what is this project?", so
      // the card lands on Overview. The sidebar still goes to the board.
      to={`/projects/${project.key}/overview`}
      data-testid="project-card"
      className="vt-surface relative block p-3 transition-colors duration-[120ms] hover:bg-[var(--surface-2)]"
    >
      <span className="vt-spine" style={{ background: project.colour }} aria-hidden />
      <div className="flex items-start gap-2 pl-2">
        <span className="text-[18px] leading-none">{project.icon}</span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="vt-mono text-[11px] vt-muted">{project.key}</span>
            {project.status === 'archived' && <Badge tone="neutral">Archived</Badge>}
            {project.visibility === 'private' && <Badge tone="neutral">Private</Badge>}
            {project.visibility === 'guest_shared' && <Badge tone="warn">Guest shared</Badge>}
          </div>
          <h2 className="truncate text-[15px] font-medium">{project.name}</h2>
          {project.description ? (
            <p className="mt-0.5 line-clamp-2 text-[12px] vt-muted">{project.description}</p>
          ) : null}
        </div>
      </div>

      <dl className="mt-3 flex gap-4 pl-2 text-[11px] vt-muted">
        <div>
          <dt className="inline">Open </dt>
          <dd className="vt-mono inline font-medium text-[var(--text)]">{stats?.openTasks ?? 0}</dd>
        </div>
        <div>
          <dt className="inline">Overdue </dt>
          <dd
            className="vt-mono inline font-medium"
            style={{ color: stats?.overdueTasks ? 'var(--danger)' : 'var(--text)' }}
          >
            {stats?.overdueTasks ?? 0}
          </dd>
        </div>
        <div>
          <dt className="inline">Blocked </dt>
          <dd className="vt-mono inline font-medium text-[var(--text)]">{stats?.blockedTasks ?? 0}</dd>
        </div>
      </dl>
    </Link>
  );
}

export function ProjectsPage() {
  const can = useCan();
  const [includeArchived, setIncludeArchived] = useState(false);
  const setCreateOpen = useUiStore((state) => state.setCreateProjectOpen);
  const { data: projects, isLoading } = useProjects({ includeArchived });

  return (
    <>
      <PageHeader
        title="Projects"
        subtitle="Every product and workstream in the workspace."
        actions={
          <>
            <Button
              variant={includeArchived ? 'subtle' : 'ghost'}
              size="sm"
              onClick={() => setIncludeArchived((v) => !v)}
            >
              <Archive />
              {includeArchived ? 'Hiding nothing' : 'Show archived'}
            </Button>
            {can(PERMISSIONS.PROJECT_CREATE) && (
              <Button variant="primary" data-testid="new-project" onClick={() => setCreateOpen(true)}>
                <FolderPlus />
                New project
              </Button>
            )}
          </>
        }
      />

      <div className="p-4">
        {isLoading ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-28" />
            ))}
          </div>
        ) : !projects || projects.length === 0 ? (
          <EmptyState
            icon={<FolderPlus className="size-6" />}
            title="No projects yet"
            description="A project holds its own statuses, sprints and tasks. Start with one product."
            action={
              can(PERMISSIONS.PROJECT_CREATE) ? (
                <Button variant="primary" onClick={() => setCreateOpen(true)}>
                  <FolderPlus />
                  Create the first project
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {projects.map((project) => (
              <ProjectCard key={project._id} project={project} />
            ))}
          </div>
        )}
      </div>

    </>
  );
}
